import { useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api, ApiClientError } from '../lib/api';
import { Badge, Button, Input, Select, Checkbox } from '../atoms';
import { useToast } from './Toast';
import { useEntityPermission } from '../lib/usePermissions';
import { CreateEntityModal } from './CreateEntityModal';
import {
  CustomFieldDefinitionModal,
  dataTypeLabel,
  type CustomFieldDefinition,
} from './CustomFieldDefinitionModal';
import { ApplyCustomFieldModal } from './ApplyCustomFieldModal';
import { ReuseCustomFieldModal } from './ReuseCustomFieldModal';

/**
 * CustomFieldsPanel — the per-record custom-fields tab.
 *
 * Two responsibilities, split by scope:
 *  1. VALUE EDITOR (every scope): fetches the scope-resolved active fields for
 *     one record from GET /api/custom-fields/:entityType/:entityId (definitions
 *     merged with the record's current values), renders a control per data type,
 *     and saves each dirty field via PUT /api/custom-fields/:entityType/:entityId.
 *     Value editing is gated on the PARENT entity's can_edit (a custom field is
 *     part of the record).
 *  2. MANAGE FIELDS (clients + properties only, PRD 2.6a): create / edit /
 *     activate / reorder the field DEFINITIONS scoped to this profile, plus
 *     "Apply to other clients…" (2.6c) — without leaving the profile. Parcels
 *     cannot OWN a definition (resolveScope only yields client/property), so the
 *     parcel tab stays a value editor with a caption pointing to where fields
 *     are defined. Management controls are gated on the dedicated `custom_field`
 *     permission (matching the server router), so a user who can enter values
 *     but not change the schema sees the editor without the manage controls.
 *
 * Definition changes invalidate BOTH the panel's value query and the shared
 * ['custom-field-definitions', …] list key (the same one the central Systems
 * page CustomFieldsAdmin uses), so the master list and the profile stay in
 * lockstep automatically.
 *
 * Domain 19 / PRD 2.6 — Per-Client Custom Fields.
 */

interface CustomFieldValue {
  definition_id: string;
  field_key: string;
  label: string;
  data_type: 'text' | 'number' | 'date' | 'boolean' | 'select';
  options: string[] | null;
  display_order: number;
  scope: 'client' | 'property';
  value: string | null;
}

// Plural table name -> the singular permission-grid entity name.
const ENTITY_SINGULAR: Record<string, string> = {
  parcels: 'parcel',
  clients: 'client',
  properties: 'property',
};

export interface CustomFieldsPanelProps {
  entityType: string;
  entityId: string;
}

export function CustomFieldsPanel({ entityType, entityId }: CustomFieldsPanelProps) {
  // Only clients + properties can OWN a definition scope (services/custom-fields
  // resolveScope). Parcels inherit their property/client fields — value-only.
  const canOwnScope = entityType === 'clients' || entityType === 'properties';

  if (!canOwnScope) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-3)' }}>
        <p style={{ margin: 0, color: 'var(--color-muted)', fontSize: 'var(--fs-sm)' }}>
          Custom fields are defined on the property or client. Here you can fill in their values.
        </p>
        <CustomFieldValueEditor entityType={entityType} entityId={entityId} />
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-5)', minWidth: 0, maxWidth: '100%' }}>
      <ManageFieldsRegion entityType={entityType} entityId={entityId} />
      <CustomFieldValueEditor entityType={entityType} entityId={entityId} hideEmpty />
    </div>
  );
}

// ── Manage fields (definition CRUD in-profile) ──────────────────────────────

function ManageFieldsRegion({ entityType, entityId }: { entityType: string; entityId: string }) {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const [createOpen, setCreateOpen] = useState(false);
  const [reuseOpen, setReuseOpen] = useState(false);
  const [editId, setEditId] = useState<string | null>(null);
  const [applyField, setApplyField] = useState<CustomFieldDefinition | null>(null);

  const canAdd = useEntityPermission('custom_field', 'can_add');
  const canEditSchema = useEntityPermission('custom_field', 'can_edit');

  const isClient = entityType === 'clients';
  const scopeParam = isClient ? `client_id=${entityId}` : `property_id=${entityId}`;
  const scopeLabel = isClient ? 'this client' : 'this property';

  // Manage list uses the SAME key shape CustomFieldsAdmin uses
  // (['custom-field-definitions', clientId, propertyId]) so the Systems master
  // list and this profile share cache and stay in lockstep.
  const listKey = isClient
    ? ['custom-field-definitions', entityId, '']
    : ['custom-field-definitions', '', entityId];

  const defsQuery = useQuery<CustomFieldDefinition[]>({
    queryKey: listKey,
    queryFn: ({ signal }) =>
      api.get<CustomFieldDefinition[]>(`/api/custom-field-definitions?${scopeParam}&sort=display_order&dir=asc`, signal),
  });

  const definitions = defsQuery.data ?? [];
  const existingKeys = useMemo(() => definitions.map((d) => d.field_key), [definitions]);

  // On ANY definition change, invalidate the value editor, every definition list
  // (broad prefix → the Systems page + this profile), and the reuse catalog.
  function invalidateAll() {
    queryClient.invalidateQueries({ queryKey: ['custom-fields', entityType, entityId] });
    queryClient.invalidateQueries({ queryKey: ['custom-field-definitions'] });
    queryClient.invalidateQueries({ queryKey: ['custom-field-catalog'] });
  }

  const toggleActive = useMutation({
    mutationFn: (def: CustomFieldDefinition) =>
      api.put(`/api/custom-field-definitions/${def.id}`, { is_active: !def.is_active }),
    onSuccess: (_data, def) => {
      invalidateAll();
      toast({ variant: 'success', message: `Field ${def.is_active ? 'deactivated' : 'reactivated'}.` });
    },
    onError: () => toast({ variant: 'error', message: 'Could not update the field.' }),
  });

  const reorder = useMutation({
    mutationFn: (orderedIds: string[]) =>
      api.put('/api/custom-fields/definitions/reorder', { ordered_ids: orderedIds }),
    onSuccess: () => invalidateAll(),
    onError: (err) => toast({ variant: 'error', message: err instanceof Error ? err.message : 'Reorder failed.' }),
  });

  function move(id: string, dir: -1 | 1) {
    const ids = definitions.map((d) => d.id);
    const idx = ids.indexOf(id);
    const swap = idx + dir;
    if (idx < 0 || swap < 0 || swap >= ids.length) return;
    [ids[idx], ids[swap]] = [ids[swap], ids[idx]];
    reorder.mutate(ids);
  }

  const busy = toggleActive.isPending || reorder.isPending;

  return (
    <div style={{
      background: 'var(--color-card)',
      border: '1px solid var(--color-border)',
      borderRadius: 'var(--r-lg)',
      padding: 'var(--sp-4)',
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 'var(--sp-4)' }}>
        <div>
          <h3 style={{
            margin: 0,
            fontSize: 'var(--fs-xs)',
            fontWeight: 'var(--fw-semibold)',
            textTransform: 'uppercase',
            letterSpacing: 'var(--ls-wider)',
            color: 'var(--color-ink-3)',
          }}>Manage fields</h3>
          <p style={{ margin: 'var(--sp-1) 0 0', color: 'var(--color-muted)', fontSize: 'var(--fs-sm)' }}>
            Define the custom fields for {scopeLabel}. They also appear on the central Custom fields page.
          </p>
        </div>
        {canAdd && (
          <div style={{ display: 'flex', gap: 'var(--sp-2)', flexShrink: 0 }}>
            {/* N2 — pull an existing field onto this scope instead of recreating it. */}
            <Button variant="secondary" size="sm" onClick={() => setReuseOpen(true)}>
              Reuse existing…
            </Button>
            <Button variant="primary" size="sm" onClick={() => setCreateOpen(true)}>
              + Add field
            </Button>
          </div>
        )}
      </div>

      {defsQuery.isLoading ? (
        <p style={{ color: 'var(--color-ink-3)' }}>Loading fields…</p>
      ) : defsQuery.error ? (
        <div style={{
          padding: 'var(--sp-4)',
          background: 'var(--color-status-red-bg)',
          border: '1px solid var(--color-status-red)',
          color: 'var(--color-status-red)',
          borderRadius: 'var(--r-md)',
        }}>
          Failed to load fields. {(defsQuery.error as Error).message}
        </div>
      ) : definitions.length === 0 ? (
        <div style={{
          padding: 'var(--sp-5)',
          border: '1px dashed var(--color-border)',
          borderRadius: 'var(--r-md)',
          color: 'var(--color-muted)',
          textAlign: 'center',
          fontSize: 'var(--fs-sm)',
        }}>
          No custom fields for {scopeLabel} yet.{canAdd ? ' Add the first one.' : ''}
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-2)' }}>
          {definitions.map((d, idx) => (
            <div key={d.id} style={{
              display: 'flex',
              alignItems: 'center',
              gap: 'var(--sp-3)',
              // Let the badges + action buttons wrap under the field name on a
              // narrow screen instead of overflowing the row → the whole page.
              flexWrap: 'wrap',
              padding: 'var(--sp-2) var(--sp-3)',
              border: '1px solid var(--color-border)',
              borderRadius: 'var(--r-md)',
              background: d.is_active ? 'var(--color-bg)' : 'transparent',
            }}>
              {canEditSchema && (
                <div style={{ display: 'flex', gap: 2 }}>
                  <Button variant="ghost" size="sm" disabled={busy || idx === 0} onClick={() => move(d.id, -1)} aria-label="Move up">↑</Button>
                  <Button variant="ghost" size="sm" disabled={busy || idx === definitions.length - 1} onClick={() => move(d.id, 1)} aria-label="Move down">↓</Button>
                </div>
              )}
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontWeight: 'var(--fw-medium)', color: 'var(--color-ink)' }}>{d.label}</div>
                <div style={{ fontFamily: 'var(--font-mono)', fontSize: 'var(--fs-xs)', color: 'var(--color-muted)' }}>{d.field_key}</div>
              </div>
              <Badge variant="neutral">{dataTypeLabel[d.data_type]}</Badge>
              {d.is_active
                ? <Badge variant="success" dot>Active</Badge>
                : <Badge variant="neutral" dot>Inactive</Badge>}
              <div style={{ display: 'flex', gap: 'var(--sp-2)', marginLeft: 'auto' }}>
                {canEditSchema && (
                  <Button variant="secondary" size="sm" onClick={() => setEditId(d.id)}>Edit</Button>
                )}
                {canEditSchema && (
                  <Button variant="ghost" size="sm" disabled={busy} onClick={() => toggleActive.mutate(d)}>
                    {d.is_active ? 'Deactivate' : 'Reactivate'}
                  </Button>
                )}
                {isClient && canAdd && (
                  <Button variant="ghost" size="sm" onClick={() => setApplyField(d)}>
                    Apply to other clients…
                  </Button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      <CustomFieldDefinitionModal
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        clientId={isClient ? entityId : undefined}
        propertyId={isClient ? undefined : entityId}
        scopeLabel={scopeLabel}
        existingKeys={existingKeys}
        onCreated={() => { invalidateAll(); setCreateOpen(false); }}
      />

      {/* N2 — the PULL: browse fields that already exist in the org and apply one here,
          without leaving the profile or recreating a duplicate. */}
      <ReuseCustomFieldModal
        open={reuseOpen}
        onClose={() => setReuseOpen(false)}
        entityType={isClient ? 'clients' : 'properties'}
        entityId={entityId}
        existingKeys={existingKeys}
        scopeLabel={scopeLabel}
        onApplied={invalidateAll}
      />

      <CreateEntityModal
        open={!!editId}
        onClose={() => { setEditId(null); invalidateAll(); }}
        table="custom_field_definitions"
        title="Edit custom field"
        recordId={editId ?? undefined}
        invalidateKeys={[
          ['custom-field-definitions'],
          ['custom-fields', entityType, entityId],
          ['custom-field-catalog'],
        ]}
      />

      {applyField && (
        <ApplyCustomFieldModal
          open={!!applyField}
          onClose={() => setApplyField(null)}
          field={{
            label: applyField.label,
            data_type: applyField.data_type,
            options: applyField.options_json ? safeOptions(applyField.options_json) : null,
          }}
          currentClientId={entityId}
        />
      )}
    </div>
  );
}

function safeOptions(json: string): string[] | null {
  try { const v = JSON.parse(json); return Array.isArray(v) ? v.map((o) => String(o)) : null; }
  catch { return null; }
}

// ── Value editor (enter values for the active fields) ───────────────────────

function CustomFieldValueEditor({
  entityType, entityId, hideEmpty = false,
}: {
  entityType: string;
  entityId: string;
  hideEmpty?: boolean;
}) {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const [edits, setEdits] = useState<Record<string, string>>({});

  const canEdit = useEntityPermission(ENTITY_SINGULAR[entityType] ?? entityType, 'can_edit');

  const queryKey = ['custom-fields', entityType, entityId];
  const fieldsQuery = useQuery<CustomFieldValue[]>({
    queryKey,
    queryFn: ({ signal }) => api.get<CustomFieldValue[]>(`/api/custom-fields/${entityType}/${entityId}`, signal),
    enabled: !!entityType && !!entityId,
  });

  const fields = useMemo(() => fieldsQuery.data ?? [], [fieldsQuery.data]);

  const dirty = useMemo(
    () => fields.filter((f) => f.definition_id in edits && edits[f.definition_id] !== (f.value ?? '')),
    [fields, edits],
  );

  const save = useMutation({
    mutationFn: async () => {
      for (const f of dirty) {
        await api.put(`/api/custom-fields/${entityType}/${entityId}`, {
          definition_id: f.definition_id,
          value: edits[f.definition_id],
        });
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey });
      setEdits({});
      toast({ variant: 'success', message: `Saved ${dirty.length} ${dirty.length === 1 ? 'change' : 'changes'}.` });
    },
    onError: (err) => {
      const msg = err instanceof ApiClientError && typeof err.body === 'object' && err.body && 'error' in err.body
        ? String((err.body as { error: string }).error)
        : 'Save failed.';
      toast({ variant: 'error', message: msg });
    },
  });

  if (fieldsQuery.isLoading) {
    return <p style={{ color: 'var(--color-ink-3)' }}>Loading custom fields…</p>;
  }

  if (fieldsQuery.error) {
    return (
      <div style={{
        padding: 'var(--sp-5)',
        background: 'var(--color-status-red-bg)',
        border: '1px solid var(--color-status-red)',
        color: 'var(--color-status-red)',
        borderRadius: 'var(--r-lg)',
      }}>
        Failed to load custom fields. {(fieldsQuery.error as Error).message}
      </div>
    );
  }

  if (fields.length === 0) {
    if (hideEmpty) return null;
    return (
      <div style={{
        padding: 'var(--sp-6)',
        background: 'var(--color-card)',
        border: '1px dashed var(--color-border)',
        borderRadius: 'var(--r-lg)',
        color: 'var(--color-muted)',
        textAlign: 'center',
        fontSize: 'var(--fs-md)',
      }}>No custom fields configured for this {ENTITY_SINGULAR[entityType] ?? 'record'}.</div>
    );
  }

  function setValue(id: string, value: string) {
    setEdits((prev) => ({ ...prev, [id]: value }));
  }

  function currentValue(f: CustomFieldValue): string {
    return f.definition_id in edits ? edits[f.definition_id] : (f.value ?? '');
  }

  return (
    <div style={{
      background: 'var(--color-card)',
      border: '1px solid var(--color-border)',
      borderRadius: 'var(--r-lg)',
      padding: 'var(--sp-4)',
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 'var(--sp-4)' }}>
        <h3 style={{
          margin: 0,
          fontSize: 'var(--fs-xs)',
          fontWeight: 'var(--fw-semibold)',
          textTransform: 'uppercase',
          letterSpacing: 'var(--ls-wider)',
          color: 'var(--color-ink-3)',
        }}>Field values</h3>
        {canEdit && (
          <Button
            variant="primary"
            size="sm"
            disabled={dirty.length === 0 || save.isPending}
            onClick={() => save.mutate()}
          >
            {save.isPending ? 'Saving…' : dirty.length > 0 ? `Save ${dirty.length} ${dirty.length === 1 ? 'change' : 'changes'}` : 'Save'}
          </Button>
        )}
      </div>

      <div style={{
        display: 'grid',
        // min(100%, 280px) so a viewport narrower than 280px collapses the track
        // to full width instead of forcing a 280px column that overflows the page.
        gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 280px), 1fr))',
        gap: 'var(--sp-4)',
      }}>
        {fields.map((f) => (
          <div key={f.definition_id} style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-1)' }}>
            <span style={{
              display: 'flex', alignItems: 'center', gap: 'var(--sp-2)',
              fontSize: 'var(--fs-sm)', color: 'var(--color-ink-3)', fontWeight: 'var(--fw-medium)',
            }}>
              {f.label}
              {f.scope === 'property' && <Badge variant="info">Property</Badge>}
            </span>
            <FieldControl field={f} value={currentValue(f)} readOnly={!canEdit} onChange={(v) => setValue(f.definition_id, v)} />
          </div>
        ))}
      </div>
    </div>
  );
}

function FieldControl({
  field, value, readOnly, onChange,
}: {
  field: CustomFieldValue;
  value: string;
  readOnly: boolean;
  onChange: (value: string) => void;
}) {
  if (field.data_type === 'boolean') {
    return (
      <Checkbox
        label={value === 'true' ? 'Yes' : 'No'}
        checked={value === 'true'}
        disabled={readOnly}
        onChange={(e) => onChange((e.target as HTMLInputElement).checked ? 'true' : 'false')}
      />
    );
  }
  if (field.data_type === 'select') {
    const options = [
      { value: '', label: '—' },
      ...(field.options ?? []).map((o) => ({ value: o, label: o })),
    ];
    return <Select options={options} value={value} disabled={readOnly} onChange={(e) => onChange(e.target.value)} />;
  }
  const inputType = field.data_type === 'number' ? 'number' : field.data_type === 'date' ? 'date' : 'text';
  return <Input type={inputType} value={value} disabled={readOnly} onChange={(e) => onChange(e.target.value)} />;
}
